import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ScenarioDifficulty, type ScenarioHomePreview} from '@types';
import {OverlayTrigger} from '@goproblems/ui';
import ProgressPopover from '@components/ProgressPopover';

const difficulties = Object.values(ScenarioDifficulty);

const InvasionDifficultyProgress = ({
    summary,
}: {
    summary?: Pick<
        ScenarioHomePreview,
        'userSolvedDifficulties' | 'userTriedDifficulties'
    >;
}) => {
    const {t} = useTranslation();
    const [show, setShow] = useState(false);
    const solved = summary?.userSolvedDifficulties ?? [];
    const tried = summary?.userTriedDifficulties ?? [];
    const solvedCount = difficulties.filter(difficulty =>
        solved.includes(difficulty)
    ).length;
    const title = `${t('levels.progress')} ${solvedCount}/${difficulties.length}`;
    const metrics = difficulties.map(difficulty => ({
        label: difficulty,
        value: t(
            solved.includes(difficulty)
                ? 'invasions.solved'
                : tried.includes(difficulty)
                  ? 'invasions.tried'
                  : 'invasions.not_tried'
        ),
    }));

    return (
        <OverlayTrigger
            show={show}
            onToggle={setShow}
            rootClose
            placement="top"
            wrapperClassName="w-full shrink-0"
            overlay={<ProgressPopover title={title} metrics={metrics} />}>
            <button
                type="button"
                className="invasion-difficulty-progress flex w-full items-center gap-0.5 rounded-sm py-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-400"
                aria-label={`${title}. ${metrics.map(metric => `${metric.label}: ${metric.value}`).join(', ')}`}
                onClick={() => setShow(true)}
                onKeyDown={event => {
                    if (event.key === 'Escape') setShow(false);
                }}>
                {difficulties.map(difficulty => (
                    <span
                        key={difficulty}
                        aria-hidden="true"
                        className={`h-1 min-w-0 flex-1 rounded-full ${
                            solved.includes(difficulty)
                                ? 'bg-green-600 dark:bg-green-500'
                                : 'bg-neutral-200 dark:bg-neutral-700'
                        }`}
                    />
                ))}
            </button>
        </OverlayTrigger>
    );
};

export default InvasionDifficultyProgress;
